import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import PreservationModel from '@/models/PreservationModel';
import store from '@/store';
import { PreservationsResponse } from '@/types/surelockResponses';
import Api from '@/store/surelockApi';
import { VALID_PRESERVATION_STATUSES } from '@/utils/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Vue from 'vue';
dayjs.extend(utc);

function formatPreservationDates(records: Array<PreservationModel>): void {
  for (const preservation of records) {
    preservation.created = dayjs.utc(preservation.created).format('MMM-DD-YYYY HH:mm');
    preservation.modified = dayjs.utc(preservation.modified).format('MMM-DD-YYYY HH:mm');
  }
}

// The API is sorted newest-first, so its _links.next points to the next page of
// older records and _links.prev points back to newer records.
function extractPageLinks(data: PreservationsResponse): { olderHref: string; newerHref: string } {
  return {
    olderHref: data._links?.next?.href ?? '',
    newerHref: data._links?.prev?.href ?? '',
  };
}

@Module({
  name: 'preservations',
  namespaced: true,
  dynamic: true,
  store,
})
export class PreservationsModule extends VuexModule {
  _values: Array<PreservationModel> = [];
  olderHref = '';
  newerHref = '';
  filteredStatus = '';

  get filtered(): Array<PreservationModel> {
    if (!this.filteredStatus || this.filteredStatus === 'ANY') {
      return this._values;
    }
    return this._values.filter(v => v.status === this.filteredStatus);
  }

  @Mutation
  receivePreservations(data: PreservationsResponse): void {
    Vue.set(this, '_values', data.preservations.map((v, i) => {
      return {
        ...v,
        index: i,
      };
    }));
    const { olderHref, newerHref } = extractPageLinks(data);
    this.olderHref = olderHref;
    this.newerHref = newerHref;
  }

  @Mutation
  appendPreservations(data: PreservationsResponse): void {
    const appended = data.preservations.map((v, i) => ({
      ...v,
      index: this._values.length + i,
    }));
    Vue.set(this, '_values', [...this._values, ...appended]);
    const { olderHref, newerHref } = extractPageLinks(data);
    this.olderHref = olderHref;
    this.newerHref = newerHref;
  }

  @Mutation
  filter(status: string): void {
    this.filteredStatus = status;
  }

  @Mutation
  reset(): void {
    this._values = [];
    this.olderHref = '';
    this.newerHref = '';
  }

  @Action({ commit: 'receivePreservations', rawError: true })
  async getPreservations(): Promise<PreservationsResponse> {
    const data = await Api.getPreservationRecords();
    formatPreservationDates(data.preservations);
    return data;
  }

  @Action({ commit: 'appendPreservations', rawError: true })
  async getOlderPreservations(): Promise<PreservationsResponse> {
    if (!this.olderHref) return { preservations: [], _links: { prev: { href: '', method: 'GET' }, next: { href: '', method: 'GET' } } };
    const data = await Api.makeGetRequest(this.olderHref);
    formatPreservationDates(data.preservations);
    return data;
  }

  @Action({ commit: 'filter' })
  updateStatusFilter(status: string): string {
    if (VALID_PRESERVATION_STATUSES.includes(status)) {
      return status;
    }
    return 'ANY';
  }
}

export default getModule(PreservationsModule);
